import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import { Activity, Company, Deal, Group, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Note from "@mapmycustomers/shared/types/entity/common/Note";
import Route from "@mapmycustomers/shared/types/entity/Route";
import UpdatePersonPayload from "@mapmycustomers/shared/types/entity/UpdatePersonPayload";
import { RawFile } from "@mapmycustomers/shared/types/File";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import { OnlyRequiredFields } from "@mapmycustomers/shared/util/ts";

import RecapRange from "@app/enum/preview/RecapRange";
import RecordPaneTab from "@app/enum/preview/RecordPaneTab";
import { FieldValidationErrors } from "@app/store/api/ApiService";
import FileListItem from "@app/types/FileListItem";

import PersonRecordData from "./PersonRecordData";

export const fetchPreviewData = createAsyncAction(
  "personRecord/fetchPreviewData/request",
  "personRecord/fetchPreviewData/success",
  "personRecord/fetchPreviewData/failure"
)<
  Person["id"],
  {
    recordData: PersonRecordData;
    selectedActivityTypes: ActivityType[];
  },
  void
>();

export const fetchPersonActivitiesCompletedByType = createAsyncAction(
  "personRecord/fetchPersonActivitiesCompletedByType/request",
  "personRecord/fetchPersonActivitiesCompletedByType/success",
  "personRecord/fetchPersonActivitiesCompletedByType/failure"
)<
  {
    activityTypes: ActivityType[];
    filter: ActivityStatusOption;
    order: SortModel;
    personId: Person["id"];
    recapRange: RecapRange;
    selectedAssignees: User["id"][];
  },
  Activity[],
  void
>();

export const fetchThumbnail = createAction("personRecord/thumbnail")<{
  callback: (data: Blob) => void;
  fileId: RawFile["id"];
}>();

export const changeActivitiesAssignees = createAction("personRecord/changeActivitiesAssignees")<
  User["id"][]
>();

export const changeActivitiesOrder = createAction(
  "personRecord/changeActivitiesOrder"
)<SortModel>();

export const fetchPersonActivities = createAsyncAction(
  "personRecord/fetchPersonActivities/request",
  "personRecord/fetchPersonActivities/success",
  "personRecord/fetchPersonActivities/failure"
)<
  {
    activityTypes: ActivityType[];
    filter: ActivityStatusOption;
    order: SortModel;
    personId: Person["id"];
    recapRange: RecapRange;
    search: string;
    selectedAssignees: User["id"][];
  },
  Activity[],
  void
>();

export const fetchPersonActivitiesTotal = createAsyncAction(
  "personRecord/fetchPersonActivitiesTotal/request",
  "personRecord/fetchPersonActivitiesTotal/success",
  "personRecord/fetchPersonActivitiesTotal/failure"
)<Person["id"], number, void>();

export const fetchPerson = createAsyncAction(
  "personRecord/fetchPerson/request",
  "personRecord/fetchPerson/success",
  "personRecord/fetchPerson/failure"
)<Person, Person, void>();

export const fetchPersonRoutes = createAsyncAction(
  "personRecord/fetchPersonRoutes/request",
  "personRecord/fetchPersonRoutes/success",
  "personRecord/fetchPersonRoutes/failure"
)<Person["id"], Route[], void>();

export const createPersonNote = createAsyncAction(
  "personRecord/createPersonNote/request",
  "personRecord/createPersonNote/success",
  "personRecord/createPersonNote/failure"
)<string, void, void>();

export const updatePersonNote = createAsyncAction(
  "personRecord/updatePersonNote/request",
  "personRecord/updatePersonNote/success",
  "personRecord/updatePersonNote/failure"
)<Note, Note, Note["id"]>();

export const deletePersonNote = createAsyncAction(
  "personRecord/deletePersonNote/request",
  "personRecord/deletePersonNote/success",
  "personRecord/deletePersonNote/failure"
)<Note, Note["id"], Note["id"]>();

export const uploadPersonFiles = createAsyncAction(
  "personRecord/uploadPersonFiles/request",
  "personRecord/uploadPersonFiles/success",
  "personRecord/uploadPersonFiles/failure"
)<
  {
    callback?: (filesList: FileListItem[]) => void;
    fileGroupId?: string;
    files: (Blob | File)[];
  },
  FileListItem[],
  void
>();

export const deletePersonFile = createAsyncAction(
  "personRecord/deletePersonFile/request",
  "personRecord/deletePersonFile/success",
  "personRecord/deletePersonFile/failure"
)<RawFile, { file: RawFile; removed: boolean }, RawFile>();

export const clearAllUploadedPersonFiles = createAction(
  "personRecord/clearAllUploadedPersonFiles"
)<void>();

export const downloadPersonFile = createAction("personRecord/downloadPersonFile")<RawFile>();

export const updatePerson = createAsyncAction(
  "personRecord/updatePerson/request",
  "personRecord/updatePerson/success",
  "personRecord/updatePerson/failure"
)<
  {
    callback?: (updatedPerson: Person) => void;
    customFields?: Record<CustomField["esKey"], CustomFieldValue>;
    fieldsValueValidateCallback?: (fieldErrors: FieldValidationErrors[]) => void;
    groupIdsToAdd?: Group["id"][];
    groupIdsToRemove?: Group["id"][];
    layoutId?: FormLayout["id"];
    person: OnlyRequiredFields<
      Pick<
        UpdatePersonPayload,
        | "address"
        | "cadenceInterval"
        | "city"
        | "color"
        | "country"
        | "countryCode"
        | "crmActivityTypeId"
        | "email"
        | "geoAddress"
        | "geoManagementState"
        | "geoPoint"
        | "id"
        | "phone"
        | "postalCode"
        | "region"
        | "user"
      >,
      "id"
    >;
  },
  { companies?: Company[]; person: Person; totalCompanies?: number },
  void
>();

export const addPersonCompanies = createAsyncAction(
  "personRecord/addPersonCompanies/request",
  "personRecord/addPersonCompanies/success",
  "personRecord/addPersonCompanies/failure"
)<
  {
    companyIds: Company["id"][];
    failureCallback?: () => void;
    id: Person["id"];
    primaryCompanyId?: Company["id"];
    removedCompanyIds?: Company["id"][];
  },
  { companies: Company[]; total: number },
  void
>();

export const removePersonCompany = createAsyncAction(
  "personRecord/removePersonCompany/request",
  "personRecord/removePersonCompany/success",
  "personRecord/removePersonCompany/failure"
)<{ companyId: Company["id"]; id: Person["id"]; layoutId: FormLayout["id"] }, Company[], void>();

export const addPersonDeals = createAsyncAction(
  "personRecord/addPersonDeals/request",
  "personRecord/addPersonDeals/success",
  "personRecord/addPersonDeals/failure"
)<
  { dealIds?: Array<Deal["id"]>; id: Person["id"]; removedDealsIds?: Deal["id"][] },
  { deals: Deal[]; total: number },
  void
>();

export const removePersonDeal = createAsyncAction(
  "personRecord/removePersonDeal/request",
  "personRecord/removePersonDeal/success",
  "personRecord/removePersonDeal/failure"
)<Deal["id"], Deal["id"], Deal["id"]>();

export const removePersonFromRoute = createAsyncAction(
  "personRecord/removePersonFromRoute/request",
  "personRecord/removePersonFromRoute/success",
  "personRecord/removePersonFromRoute/failure"
)<{ id: Person["id"]; routeId: Route["id"] }, Route["id"], Route["id"]>();

export const toggleCompleteActivity = createAsyncAction(
  "personRecord/toggleCompleteActivity/request",
  "personRecord/toggleCompleteActivity/success",
  "personRecord/toggleCompleteActivity/failure"
)<Activity, Pick<Activity, "completed" | "completedAt" | "id">, Activity["id"]>();

export const postponeActivity = createAsyncAction(
  "personRecord/postponeActivity/request",
  "personRecord/postponeActivity/success",
  "personRecord/postponeActivity/failure"
)<Activity, Activity, Activity["id"]>();

export const updateActivityNote = createAsyncAction(
  "personRecord/updateActivityNote/request",
  "personRecord/updateActivityNote/success",
  "personRecord/updateActivityNote/failure"
)<{ activity: Activity; onSuccess: () => void }, Pick<Activity, "id" | "note">, Activity["id"]>();

export const changeActivitiesSearchQuery = createAction(
  "personRecord/changeActivitiesSearchQuery"
)<string>();
export const changeActivitiesFilter = createAction(
  "personRecord/changeActivitiesFilter"
)<ActivityStatusOption>();
export const changeActivitiesRecapRange = createAction(
  "personRecord/changeActivitiesRecapRange"
)<RecapRange>();
export const changeActivitiesSelectedActivityTypes = createAction(
  "personRecord/changeActivitiesSelectedActivityTypes"
)<ActivityType[]>();

export const deletePerson = createAsyncAction(
  "personRecord/deletePerson/request",
  "personRecord/deletePerson/success",
  "personRecord/deletePerson/failure"
)<void, void, void>();

export const setActiveTab = createAction("personRecord/setActiveTab")<RecordPaneTab>();

export const setActivityElementId = createAction("personRecord/setActivityElementId")<string>();

export const setActivityAutoScrolling = createAction(
  "personRecord/setActivityAutoScrolling"
)<boolean>();

export const setHasChangesFlag = createAction("personRecord/setHasChangesFlag")<boolean>();

export const setPersonColor = createAction("personRecord/setPersonColor")<Person["color"]>();
export const setPersonShape = createAction("personRecord/setPersonShape")<Person["shape"]>();

export const updatePersonFrequency = createAsyncAction(
  "personRecord/updatePersonFrequency/request",
  "personRecord/updatePersonFrequency/success",
  "personRecord/updatePersonFrequency/failure"
)<
  {
    personId: Person["id"];
  },
  Person,
  void
>();

export const fetchDeals = createAsyncAction(
  "personRecord/fetchDeals/request",
  "personRecord/fetchDeals/success",
  "personRecord/fetchDeals/failure"
)<{ loadMore?: boolean }, { deals: Deal[]; total: number }, void>();

export const fetchCompanies = createAsyncAction(
  "personRecord/fetchCompanies/request",
  "personRecord/fetchCompanies/success",
  "personRecord/fetchCompanies/failure"
)<void, { companies: Company[] }, void>();

export const fetchMoreCompanies = createAsyncAction(
  "personRecord/fetchMoreCompanies/request",
  "personRecord/fetchMoreCompanies/success",
  "personRecord/fetchMoreCompanies/failure"
)<void, Company[], void>();

export const updatePersonPrimaryCompany = createAsyncAction(
  "personRecord/updatePersonPrimaryCompany/request",
  "personRecord/updatePersonPrimaryCompany/success",
  "personRecord/updatePersonPrimaryCompany/failure"
)<
  {
    accounts: Person["accounts"];
    failureCallback?: () => void;
    id: Person["id"];
    primaryCompanyId: Company["id"] | null;
  },
  { companies: Company[]; total: number },
  void
>();

export const subscribePersonEmail = createAsyncAction(
  "personRecord/subscribePersonEmail/request",
  "personRecord/subscribePersonEmail/success",
  "personRecord/subscribePersonEmail/failure"
)<void, void, void>();

export const unsubscribePersonEmail = createAsyncAction(
  "personRecord/unsubscribePersonEmail/request",
  "personRecord/unsubscribePersonEmail/success",
  "personRecord/unsubscribePersonEmail/failure"
)<void, void, void>();

export const bulkUpdateFiles = createAsyncAction(
  "personRecord/bulkUpdateFiles/request",
  "personRecord/bulkUpdateFiles/success",
  "personRecord/bulkUpdateFiles/failure"
)<{ callback?: () => void; files: OnlyRequiredFields<RawFile, "id">[] }, RawFile[], unknown>();

export const fetchNotes = createAsyncAction(
  "personRecord/fetchNotes/request",
  "personRecord/fetchNotes/success",
  "personRecord/fetchNotes/failure"
)<{ filter?: FilterModel; sort?: SortModel } | void, { data: Note[]; total: number }, void>();

export const suppressPartialAddressWarning = createAsyncAction(
  "personRecord/suppressPartialAddressWarning/request",
  "personRecord/suppressPartialAddressWarning/success",
  "personRecord/suppressPartialAddressWarning/failure"
)<void, void, void>();

export type Actions = ActionType<
  | typeof addPersonCompanies
  | typeof addPersonDeals
  | typeof bulkUpdateFiles
  | typeof changeActivitiesAssignees
  | typeof changeActivitiesFilter
  | typeof changeActivitiesOrder
  | typeof changeActivitiesRecapRange
  | typeof changeActivitiesSearchQuery
  | typeof changeActivitiesSelectedActivityTypes
  | typeof clearAllUploadedPersonFiles
  | typeof createPersonNote
  | typeof deletePerson
  | typeof deletePersonFile
  | typeof deletePersonNote
  | typeof downloadPersonFile
  | typeof fetchCompanies
  | typeof fetchDeals
  | typeof fetchMoreCompanies
  | typeof fetchNotes
  | typeof fetchPerson
  | typeof fetchPersonActivities
  | typeof fetchPersonActivitiesCompletedByType
  | typeof fetchPersonActivitiesTotal
  | typeof fetchPersonRoutes
  | typeof fetchPreviewData
  | typeof fetchThumbnail
  | typeof postponeActivity
  | typeof removePersonCompany
  | typeof removePersonDeal
  | typeof removePersonFromRoute
  | typeof setActiveTab
  | typeof setActivityAutoScrolling
  | typeof setActivityElementId
  | typeof setHasChangesFlag
  | typeof setPersonColor
  | typeof setPersonShape
  | typeof subscribePersonEmail
  | typeof suppressPartialAddressWarning
  | typeof toggleCompleteActivity
  | typeof unsubscribePersonEmail
  | typeof updateActivityNote
  | typeof updatePerson
  | typeof updatePersonFrequency
  | typeof updatePersonNote
  | typeof updatePersonPrimaryCompany
  | typeof uploadPersonFiles
>;
